import React, { useState, useEffect, useMemo } from 'react';
import {
  Layout, Move, Save, Copy, Trash2, Check, AlertTriangle,
  Layers, Loader2, Settings, Type, RotateCcw,
  Smartphone, Tablet, Monitor, MonitorSmartphone, ChevronDown
} from 'lucide-react';
import { ToggleRow } from '../../ui';
import GroupedListboxSelect from '../../ui/GroupedListboxSelect';
import api from '../../../services/api';
import * as AssessmentBuilderAPI from '../../../services/assessmentBuilderApi';
import toast from 'react-hot-toast';
import {
  DEVICE_DIMENSIONS, QUESTION_TYPE_DISPLAY, displayQuestionType,
  FONT_FAMILY_OPTIONS, LINE_SPACING_PRESETS, LINE_SPACING_MIN, LINE_SPACING_MAX, LINE_SPACING_STEP,
  DEFAULT_GLOBAL_FORMATTING
} from './constants';
import NumericInput from './NumericInput';
import TemplateManagerModal from './TemplateManagerModal';


const TEMPLATE_VRS_RESPONSE_KEYS = [
  'vrsButtonHeight',
  'vrsButtonMinWidth',
  'vrsFontSize',
  'vrsFontWeight',
  'vrsBorderRadius',
  'vrsBorderWidth',
  'vrsSelectedColor',
  'vrsUnselectedColor',
  'vrsSelectedTextColor',
  'vrsUnselectedTextColor',
  'vrsBorderColor',
  'vrsSelectedBorderColor',
  'vrsHoverColor',
  'vrsHoverBorderColor'
];

// VAS / NRS slider rendering reads these off responseConfig. They have to be
// in the template whitelist or saving a VAS layout strips the gradient + anchors.
const TEMPLATE_VAS_RESPONSE_KEYS = [
  'colorGradient',
  'leftAnchor',
  'rightAnchor',
  // Where the anchors sit, not just what they say. Without these a template
  // carried the anchor text but dropped its placement, so applying it reset
  // every question back to the default centered position.
  'anchorPlacement',
  'leftAnchorX',
  'leftAnchorY',
  'rightAnchorX',
  'rightAnchorY',
  'min',
  'max',
  'step',
  'showTicks',
  'showLabels',
  'showValues',
  'showCurrentValue',
  'labelPosition',
];

const TEMPLATE_RESPONSE_OPTION_KEYS = [
  'spacing',
  'buttonStyle',
  'optionBoxWidth',
  // Option box height is a first-class sizing control (getOptionBoxHeight);
  // without it templates can neither capture nor restore option size (FECOA-732).
  'optionBoxHeight',
  'optionTextAlign',
  'optionsAlignment',
  'btnBgColor',
  'btnTextColor',
  'btnBorderColor',
  'btnSelectedBgColor',
  'btnSelectedTextColor',
  'btnSelectedBorderColor',
  'btnBorderRadius',
  'btnBorderWidth',
  'btnFontWeight',
  'optionFontSize',
  'optionPadding',
  'radioSize',
  'continuous',
  'equalWidth',
  // Grid option placement
  'columns',
  'gridColumns',
  // Canonical NRS bounds + scale orientation/tick indicators (canvas and
  // participant renderers read minValue/maxValue/showIndicators, not min/max/
  // showTicks — those legacy spellings are translated below).
  'minValue',
  'maxValue',
  'orientation',
  'showIndicators',
  // Text input
  'maxLength',
  'rows',
  'cols',
  'placeholder',
  'showCharCount',
  // Date
  'format',
  'showCalendar',
  // Dropdown
  'showSearch',
  // Segmented / emoji scales
  'segmentHeight',
  'activeColor',
  'faceSize',
  // Body map layout (FECOA-2014): where the Selected Areas panel sits and any
  // per-region shape overrides. The edit-mode UI state (bodyMapEditMode,
  // bodyMapActiveSlug) stays out, so applying a template never drops another
  // question into edit mode.
  'selectedPanelPosition',
  'regionOverrides',
  ...TEMPLATE_VRS_RESPONSE_KEYS,
  ...TEMPLATE_VAS_RESPONSE_KEYS,
];

export const collectTemplateResponseOptions = (responseConfig = {}) => {
  // Do not default buttonStyle to 'outline': undefined means standard radio/list rendering on the
  // canvas; defaulting to outline caused saved templates to apply as boxed buttons incorrectly.
  const responseOptions = {
    display: responseConfig.layout || 'vertical',
    spacing: responseConfig.spacing ?? 8,
  };

  TEMPLATE_RESPONSE_OPTION_KEYS.forEach((key) => {
    if (responseConfig[key] !== undefined) {
      responseOptions[key] = responseConfig[key];
    }
  });

  return responseOptions;
};

// The payload persisted when an author saves the current element as a layout
// template. Extracted from the save form so the save side and the apply side can
// be tested against each other -- a template that fails to capture what the author
// configured looks correct on the element it came from and wrong on every other
// question, which is the shape QA kept reporting (FECOA-732).
export const buildTemplateLayoutConfig = (element = {}) => {
  const responseConfig = element.responseConfig || {};

  const layoutConfig = {
    position: {
      x: element.position?.x || 0,
      y: element.position?.y || 0,
      width: element.size?.width || 600,
      height: element.size?.height || 200,
    },
    responseOptions: collectTemplateResponseOptions(responseConfig),
    // The response keys this template is responsible for, captured at save time.
    // It is what lets the apply path tell "the author deliberately left this unset"
    // apart from "this template predates the field" -- the first must clear the key
    // so every question converges, the second must leave it alone so an older
    // template does not wipe a setting it never knew about.
    governedResponseKeys: [...TEMPLATE_RESPONSE_OPTION_KEYS],
    style: element.style || {},
    // The properties panel writes the author's option placement to
    // `optionsAlignment`; no control ever writes `alignment`, so reading only the
    // latter pinned every saved template to 'left' regardless of the choice made.
    alignment: responseConfig.optionsAlignment || responseConfig.alignment || 'left',
    is_required: element.required || false,
  };

  // Save free-position layout if ungrouped
  if (element.isUngrouped && element.subElements) {
    layoutConfig.isUngrouped = true;
    // Store sub-element positions relative to the element, normalized by option index
    layoutConfig.subElementLayout = element.subElements.map(sub => ({
      type: sub.type,
      optionIndex: sub.optionIndex,
      position: { ...sub.position },
      size: { ...sub.size },
      style: sub.style ? { ...sub.style } : undefined
    }));
  }

  return layoutConfig;
};

// Layout widget categories — applying a template must never silently move a question
// from one widget category to another (e.g. ordinal → text input). The canvas renderer
// keys off responseConfig.layout, so a corrupt template ({question_type: 'ordinal',
// display: 'text'}) would otherwise turn an ordinal question into a textarea on apply.
const LAYOUT_CATEGORY = {
  horizontal: 'options',
  vertical: 'options',
  grid: 'options',
  dropdown: 'options',
  boolean: 'options',
  text: 'text',
  nrs: 'scale',
  vas: 'scale',
  thermometer: 'scale',
  'vrs-unipolar': 'scale',
  'vrs-bipolar': 'scale',
  segmented: 'scale',
  emoji: 'scale',
  'stepped-slider': 'scale',
};

const mapTemplateDisplayToLayout = (responseOptions = {}, existingResponseConfig = {}) => {
  const display = responseOptions.display || '';
  const exactLayouts = new Set([
    'horizontal',
    'vertical',
    'grid',
    'dropdown',
    'text',
    'boolean',
    'vas',
    'nrs',
    'vrs-unipolar',
    'vrs-bipolar'
  ]);

  let proposed;
  if (exactLayouts.has(display)) {
    proposed = display;
  } else if (display.includes('slider')) {
    proposed = responseOptions.continuous ? 'vas' : 'nrs';
  } else if (display.includes('horizontal')) {
    proposed = 'horizontal';
  } else if (display.includes('vertical')) {
    proposed = 'vertical';
  } else if (display.includes('grid')) {
    proposed = 'grid';
  } else if (display === 'textarea' || display === 'textinput') {
    proposed = 'text';
  } else if (display === 'dropdown') {
    proposed = 'dropdown';
  } else {
    return existingResponseConfig.layout;
  }

  // Guard against bad template data: refuse to change widget category. If the
  // current layout is options-list (vertical/horizontal/dropdown) the template
  // must propose another options-list value, not a text or scale layout.
  const existingCategory = LAYOUT_CATEGORY[existingResponseConfig.layout];
  const proposedCategory = LAYOUT_CATEGORY[proposed];
  if (existingCategory && proposedCategory && existingCategory !== proposedCategory) {
    return existingResponseConfig.layout;
  }
  return proposed;
};

const buildTemplatePlacement = (config = {}, existingPosition = {}, existingSize = {}, canvasDims = {}) => {
  const canvasWidth = canvasDims.width || 1280;
  const canvasHeight = canvasDims.height || 900;
  const navButtonSpace = 100;
  const fallbackWidth = existingSize.width ?? Math.round(canvasWidth * 0.9);
  const fallbackHeight = existingSize.height ?? 220;
  const width = config.position?.width ?? fallbackWidth;
  const height = config.position?.height ?? fallbackHeight;
  const availableHeight = canvasHeight - navButtonSpace;

  return {
    position: {
      x: config.position?.x ?? existingPosition.x ?? Math.round((canvasWidth - width) / 2),
      y: config.position?.y ?? existingPosition.y ?? Math.round((availableHeight - height) / 2),
    },
    size: {
      width,
      height,
    }
  };
};

// Handled by their own merge rules above, so the template-authoritative clear below
// must leave them alone.
const EXPLICITLY_MERGED_RESPONSE_KEYS = new Set(['buttonStyle', 'spacing']);

// Option-appearance keys that have existed for as long as layout templates have.
// A template omitting one of these is genuinely not setting it -- it cannot be a
// template that predates the field -- so these clear even on templates saved before
// governedResponseKeys was recorded. Without this, re-applying a template saved in an
// earlier round still left question 2 carrying its old option styling (FECOA-1337).
const BASELINE_GOVERNED_RESPONSE_KEYS = [
  'optionsAlignment',
  'optionTextAlign',
  'optionBoxWidth',
  'optionBoxHeight',
  'optionFontSize',
  'optionPadding',
  'equalWidth',
  'radioSize',
  // Button appearance and grid placement are option appearance too, and they have
  // been captured by collectTemplateResponseOptions since the button-style controls
  // shipped -- so a template that omits them is not setting them either. Leaving
  // them ungoverned is what broke the reported case: the question the template was
  // saved from kept its own btnBorderColor/btnSelectedBgColor/etc and every other
  // question got none, so "apply to all" reported two questions updated and still
  // rendered them differently (FECOA-1337).
  'btnBgColor',
  'btnTextColor',
  'btnBorderColor',
  'btnSelectedBgColor',
  'btnSelectedTextColor',
  'btnSelectedBorderColor',
  'btnBorderRadius',
  'btnBorderWidth',
  'btnFontWeight',
  'columns',
  'gridColumns',
];

export const buildTemplateResponseConfig = (config = {}, existingResponseConfig = {}) => {
  // Newer saved templates keep the option settings under responseConfig; older/
  // system templates use responseOptions. Accept both (an array here would be a
  // response-options list, not a settings object — ignore it).
  const responseOptions =
    config.responseOptions
    || (config.responseConfig && !Array.isArray(config.responseConfig) ? config.responseConfig : null)
    || {};
  const mergedResponseConfig = {
    ...existingResponseConfig,
    layout: mapTemplateDisplayToLayout(responseOptions, existingResponseConfig),
    spacing: responseOptions.spacing ?? existingResponseConfig.spacing,
    alignment: config.alignment || existingResponseConfig.alignment,
  };

  // Only keys present on the template should override; omitted buttonStyle means radio-style options.
  if ('buttonStyle' in responseOptions) {
    mergedResponseConfig.buttonStyle = responseOptions.buttonStyle;
  } else {
    delete mergedResponseConfig.buttonStyle;
  }

  // Every key in the whitelist is template-controlled. Whatever the template says
  // wins, and a key the template does NOT define has to be cleared rather than left
  // behind from whatever this question happened to look like before. Without the
  // clear, applying one template across several questions left each one carrying its
  // own stale option settings, so question 2 kept rendering unlike question 1 no
  // matter how many times the apply path itself was fixed (FECOA-1337, FECOA-732).
  // `spacing` and `buttonStyle` are merged explicitly above and keep that handling.
  const governedKeys = new Set([
    ...BASELINE_GOVERNED_RESPONSE_KEYS,
    ...(Array.isArray(config.governedResponseKeys) ? config.governedResponseKeys : []),
  ]);
  TEMPLATE_RESPONSE_OPTION_KEYS.forEach((key) => {
    if (EXPLICITLY_MERGED_RESPONSE_KEYS.has(key)) return;
    if (responseOptions[key] !== undefined) {
      mergedResponseConfig[key] = responseOptions[key];
    } else if (governedKeys.has(key)) {
      delete mergedResponseConfig[key];
    }
  });

  // Legacy/system template spellings → the canonical responseConfig keys every
  // renderer (canvas, preview, participant) actually reads. Translations only
  // fill gaps — an explicit canonical key on the template always wins (FECOA-732).
  const fillAlias = (from, to) => {
    if (responseOptions[from] !== undefined && responseOptions[to] === undefined) {
      mergedResponseConfig[to] = responseOptions[from];
    }
  };
  // VAS/NRS anchors: system templates store leftLabel/rightLabel.
  fillAlias('leftLabel', 'leftAnchor');
  fillAlias('rightLabel', 'rightAnchor');
  // NRS bounds: renderers read minValue/maxValue, system templates store min/max.
  fillAlias('min', 'minValue');
  fillAlias('max', 'maxValue');
  // NRS tick marks: renderers read showIndicators, system templates store showTicks.
  fillAlias('showTicks', 'showIndicators');
  // Yes/no button width: renderers size options from optionBoxWidth.
  fillAlias('buttonWidth', 'optionBoxWidth');

  // Option-group placement: renderers position the options block from
  // responseConfig.optionsAlignment; system templates carry a root-level
  // `alignment`. Without this mirror, applying a centered template leaves the
  // options where they were (FECOA-732).
  if (responseOptions.optionsAlignment === undefined && config.alignment) {
    mergedResponseConfig.optionsAlignment = config.alignment;
  }
  // Scale orientation lives at the template root on system templates; VRS/NRS
  // read responseConfig.orientation.
  if (responseOptions.orientation === undefined && config.orientation) {
    mergedResponseConfig.orientation = config.orientation;
  }

  return mergedResponseConfig;
};

// Apply a layout template's config to a single canvas element, including ungrouped
// sub-element handling. Used by single-element and bulk-apply paths so they stay in sync.
// Without this, bulk apply only updates the parent element's style/position/responseConfig
// and leaves stale subElements on ungrouped questions, which the canvas renders instead
// (FECOA-1337).
export const applyTemplateConfigToElement = (el, config, canvasDims) => {
  const placement = buildTemplatePlacement(config, el.position || {}, el.size || {}, canvasDims);
  const updated = {
    ...el,
    position: placement.position,
    size: placement.size,
    style: {
      ...el.style,
      ...(config.style || {})
    },
    responseConfig: buildTemplateResponseConfig(config, el.responseConfig || {}),
    ...(config.is_required !== undefined && { required: config.is_required }),
  };

  if (config.isUngrouped && config.subElementLayout) {
    const options = el.responseOptions || el.data?.options || [];
    const textLayout = config.subElementLayout.find(s => s.type === 'question-text');
    const optLayouts = config.subElementLayout.filter(s => s.type === 'question-option');

    updated.isUngrouped = true;
    updated.subElements = [
      {
        id: `${el.id}_text`,
        type: 'question-text',
        parentId: el.id,
        content: el.data?.text || el.content || '',
        position: textLayout?.position || { x: 0, y: 0 },
        size: textLayout?.size || { width: placement.size.width, height: 60 },
        style: textLayout?.style || { ...updated.style }
      },
      ...options.map((option, idx) => {
        const tmplOpt = optLayouts[idx] || optLayouts[optLayouts.length - 1];
        const fallbackY = (textLayout?.size?.height || 60) + 20 + idx * 40;
        return {
          id: `${el.id}_option_${idx}`,
          type: 'question-option',
          parentId: el.id,
          optionIndex: idx,
          content: typeof option === 'string' ? option : (option.label || option),
          value: typeof option === 'object' ? option.value : idx,
          position: tmplOpt?.position || { x: 0, y: fallbackY },
          size: tmplOpt?.size || { width: placement.size.width, height: 32 },
          style: tmplOpt?.style || { fontSize: updated.responseConfig?.optionFontSize || 14 }
        };
      })
    ];
  } else if (el.isUngrouped) {
    updated.isUngrouped = false;
    updated.subElements = undefined;
  }

  return updated;
};

// Mirrors backend COMPATIBLE_QUESTION_TYPE_GROUPS. Templates apply across types in the
// same group; without this, a sponsor "nominal" template silently 400s on questions
// imported as 'multiple_choice' (FECOA-1337). 'numeric' and 'nrs' are one family for the
// same reason isNumericLike exists: 'numeric' is the legacy storage type for a numeric
// rating scale, the item bank still writes it for new NRS items, and both build the
// identical 0-10 rail. Ungrouped, a template saved off a 'numeric' question is stored as
// question_type='nrs' (see getEffectiveQuestionType) and then fails its own bulk-apply
// against the raw type (FECOA-1352). VAS stays out: a continuous slider is a genuinely
// different widget, and grouping it restyled true numeric questions (FECOA-1353).
const COMPATIBLE_QUESTION_TYPE_GROUPS = [
  ['nominal', 'multiple_choice', 'checkbox', 'dropdown'],
  ['yes_no', 'boolean'],
  ['numeric', 'nrs'],
];

/**
 * Resolve the ScreenQuestion ids to address a layout-template bulk-apply with.
 *
 * `screen_question_id` is a surrogate key that save_screens re-mints on every save,
 * because it deletes and recreates every row. The copy cached on a canvas element
 * therefore goes stale as soon as anything else saves: a second tab, an undo that
 * restores a pre-save snapshot, or a racing auto-save. Prefer the element to
 * screen_question_id map the latest save returned, and fall back to the cached id
 * only for elements that map did not cover (FECOA-2014).
 */
export const resolveScreenQuestionIds = (elements = [], mapping = null) => {
  const ids = [];
  (elements || []).forEach((el) => {
    const id = mapping?.[el?.id] ?? el?.screen_question_id;
    if (id) ids.push(id);
  });
  return ids;
};

export const questionTypeCompatible = (questionType, templateType) => {
  if (!questionType || !templateType) return false;
  if (questionType === templateType) return true;
  return COMPATIBLE_QUESTION_TYPE_GROUPS.some(
    group => group.includes(questionType) && group.includes(templateType)
  );
};

// Items from CDISC import / item bank don't all carry the same shape — some have
// `type`, some `response_type`, some `responseType`, some `question_type`. Resolve
// in priority order so downstream gating and matching aren't fooled by missing fields.
export const getElementQuestionType = (element) => {
  if (!element) return undefined;
  const data = element.data || {};
  return (
    data.type ||
    data.response_type ||
    data.responseType ||
    data.question_type ||
    element.questionType ||
    undefined
  );
};

// Numeric-type elements render as NRS/VAS based on responseConfig.layout. Layout templates
// are seeded under the visual type (nrs/vas), so we key lookups by layout when it's a
// first-class question type, falling back to the resolved data type otherwise. Instructions
// have type='instruction' on the element itself rather than under data.
export const getEffectiveQuestionType = (element) => {
  if (!element) return 'ordinal';
  if (element.type === 'instruction') return 'instruction';
  const dataType = getElementQuestionType(element);
  const layout = element.responseConfig?.layout;
  if (dataType === 'numeric' && (layout === 'nrs' || layout === 'vas')) return layout;
  return dataType || 'ordinal';
};

const getTemplateDeviceCategory = (template) => template?.device_category || 'all';

const matchesTemplateDevice = (template, currentDeviceCategory) => {
  const templateDeviceCategory = getTemplateDeviceCategory(template);
  return templateDeviceCategory === currentDeviceCategory || templateDeviceCategory === 'all';
};

const SaveQuestionTemplateSection = ({ selectedElementData, assessmentId, deviceMode, onTemplateSaved }) => {
  const [name, setName] = useState('');
  const [level, setLevel] = useState('assessment');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;

    const layoutConfig = buildTemplateLayoutConfig(selectedElementData);

    setSaving(true);
    try {
      await AssessmentBuilderAPI.saveQuestionLayoutTemplate({
        name: name.trim(),
        question_type: getEffectiveQuestionType(selectedElementData),
        layout_config: layoutConfig,
        level: level,
        // The responsive canvas is device-agnostic; its layout templates apply
        // to all device categories. The backend only accepts all/phone/tablet/
        // desktop, so map 'responsive' → 'all' (FECOA-1532).
        device_category: (DEVICE_DIMENSIONS[deviceMode]?.responsive ? 'all' : DEVICE_DIMENSIONS[deviceMode]?.category) || 'all',
        device_mode: deviceMode || '',
        ...(level === 'assessment' && { assessment_id: assessmentId }),
      });
      toast.success(`Saved "${name.trim()}"`);
      setName('');
      if (onTemplateSaved) onTemplateSaved();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-gray-100 pt-4 dark:border-white/[0.06]">
      <div className="mb-3 flex items-start gap-2">
        <Save className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        <div className="min-w-0">
          <h4 className="text-[13px] font-medium text-gray-900 dark:text-gray-100">Save as Template</h4>
          <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">Capture this question's layout for reuse</p>
        </div>
      </div>
      <p className="rounded-lg border border-gray-200/70 bg-gray-50 text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300 mb-3 p-3 text-xs leading-relaxed">
        This template will include the current question's layout, styling, and metadata such as requirement status{selectedElementData?.required ? <span className="font-medium text-gray-800 dark:text-gray-100"> (currently required)</span> : ''}. To change what's included, update the question settings before saving.
      </p>
      <div className="space-y-2">
        <input
          type="text"
          placeholder='Template name (e.g., "Centered Ordinal")'
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input h-9"
        />
        <div className="segmented flex w-full">
          <button
            type="button"
            onClick={() => setLevel('assessment')}
            className={`segmented-item flex-1 justify-center px-2 ${
              level === 'assessment' ? 'segmented-item-active' : ''
            }`}
          >
            This Assessment
          </button>
          <button
            type="button"
            onClick={() => setLevel('sponsor')}
            className={`segmented-item flex-1 justify-center px-2 ${
              level === 'sponsor' ? 'segmented-item-active' : ''
            }`}
          >
            Sponsor Library
          </button>
        </div>
        <button
          onClick={handleSave}
          disabled={!name.trim() || saving}
          className="btn btn-secondary btn-sm w-full"
        >
          {saving ? 'Saving...' : 'Save Template'}
        </button>
      </div>
    </div>
  );
};

// Form-wide formatting controls (FECOA-732). Sets the font family, base font size,
// and line spacing for the form. Saving cascades these onto every unlocked
// question/instruction already on the canvas and seeds newly added ones; locked
// elements keep their own styling. "Reset to Publisher Defaults" reverts the form
// values to the system template values (and re-cascades to unlocked elements).
const FormFormattingSection = ({ globalFormatting, onGlobalFormattingChange }) => {
  const current = useMemo(
    () => ({ ...DEFAULT_GLOBAL_FORMATTING, ...(globalFormatting || {}) }),
    [globalFormatting]
  );
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);

  // Re-sync the draft when the persisted value changes (e.g. after a reset),
  // unless the user is mid-edit with unsaved changes.
  const isDirty =
    draft.fontFamily !== current.fontFamily ||
    Number(draft.baseFontSize) !== Number(current.baseFontSize) ||
    Number(draft.lineSpacing) !== Number(current.lineSpacing);

  useEffect(() => {
    setDraft(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.fontFamily, current.baseFontSize, current.lineSpacing]);

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      await onGlobalFormattingChange({
        fontFamily: draft.fontFamily,
        baseFontSize: Number(draft.baseFontSize),
        lineSpacing: Number(draft.lineSpacing),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onGlobalFormattingChange({ reset: true });
    } finally {
      setSaving(false);
    }
  };

  const spacingValue = Number(draft.lineSpacing);

  return (
    <div className="card flex-shrink-0 p-4">
      <div className="mb-4 flex items-start gap-2">
        <Type className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        <div className="min-w-0">
          <h3 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Form Formatting</h3>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">Default font &amp; spacing for new questions on this form</p>
        </div>
      </div>

      <div className="space-y-3">
        {/* Font family */}
        <div>
          <label className="label">Font Family</label>
          <div className="relative">
            <select
              value={draft.fontFamily}
              onChange={(e) => setDraft(d => ({ ...d, fontFamily: e.target.value }))}
              style={{ fontFamily: draft.fontFamily }}
              className="input h-9 appearance-none pr-9"
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} style={{ fontFamily: option.value }}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>
        </div>

        {/* Base font size */}
        <div>
          <label className="label">Base Font Size (px)</label>
          <NumericInput
            value={draft.baseFontSize}
            onChange={(val) => setDraft(d => ({ ...d, baseFontSize: val }))}
            min={8}
            max={72}
            fallback={DEFAULT_GLOBAL_FORMATTING.baseFontSize}
            className="input h-9 tabular-nums"
          />
        </div>

        {/* Line spacing presets + numeric */}
        <div>
          <label className="label">Line Spacing</label>
          <div className="segmented mb-2 flex w-full">
            {LINE_SPACING_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => setDraft(d => ({ ...d, lineSpacing: preset.value }))}
                className={`segmented-item flex-1 justify-center px-1.5 tabular-nums ${
                  Math.abs(spacingValue - preset.value) < 0.001
                    ? 'segmented-item-active'
                    : ''
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <NumericInput
            value={draft.lineSpacing}
            onChange={(val) => setDraft(d => ({ ...d, lineSpacing: val }))}
            min={LINE_SPACING_MIN}
            max={LINE_SPACING_MAX}
            step={LINE_SPACING_STEP}
            float
            fallback={DEFAULT_GLOBAL_FORMATTING.lineSpacing}
            className="input h-9 tabular-nums"
          />
          <p className="hint">Between {LINE_SPACING_MIN} and {LINE_SPACING_MAX}.</p>
        </div>

        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="btn btn-primary btn-sm w-full"
        >
          {saving ? 'Saving…' : 'Save Form Formatting'}
        </button>

        <button
          onClick={handleReset}
          disabled={saving}
          className="btn btn-ghost btn-sm w-full"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to Publisher Defaults
        </button>

        <p className="rounded-lg border border-gray-200/70 bg-gray-50 text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300 p-3 text-xs leading-relaxed">
          Restyles <strong className="font-semibold text-gray-800 dark:text-gray-100">all unlocked</strong> questions and instructions on this form and sets the default for new ones. Lock an element to keep its own styling.
        </p>
      </div>
    </div>
  );
};

const LayoutPanel = ({
  selectedElementData,
  selectedElement,
  screens,
  currentScreenIndex,
  setScreens,
  buttonTemplates,
  selectedButtonTemplate,
  handleLoadButtonTemplate,
  handleSaveButtonTemplate,
  handleDeleteButtonTemplate,
  handleApplyTemplateToAllScreens,
  newTemplateName,
  setNewTemplateName,
  templateSaveLevel,
  setTemplateSaveLevel,
  setTemplateAsDefault,
  setSetTemplateAsDefault,
  calculateQuestionHeight,
  distributeOptions,
  alignOptions,
  assessmentId,
  deviceMode,
  deviceDimensions,
  handleDeviceModeChange,
  allTemplatesCache,
  refreshTemplatesCache,
  appliedDefaultTemplateIds,
  handleSave,
  onDefaultTemplateSet,
  globalFormatting,
  onGlobalFormattingChange
}) => {
  const displayType = displayQuestionType;

  // Safety checks for props
  const safeScreens = screens || [];
  const templates = buttonTemplates || [];

  // Get current screen
  const currentScreen = safeScreens[currentScreenIndex];

  // Question Layout Template state
  const [questionTemplates, setQuestionTemplates] = useState({ system: [], sponsor: [], user: [], assessment: [], other: [] });
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedQuestionTemplate, setSelectedQuestionTemplate] = useState('');
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  // Delete confirmation state (shared for button + question templates)
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, type: 'question' | 'button' }
  const [deleteTypedName, setDeleteTypedName] = useState('');

  // Get current device category from deviceMode
  const currentDeviceCategory = DEVICE_DIMENSIONS[deviceMode]?.category || 'desktop';

  // Responsive (fluid) canvas is controlled by a dedicated toggle rather than the
  // device-size dropdown — when on it "takes over" and the canvas fills the full
  // width of any screen (FECOA-1532). It is always available on every assessment.
  const isResponsiveCanvas = deviceMode === 'responsive';

  // Remember the last fixed device so toggling responsive off restores it.
  const [lastFixedDeviceMode, setLastFixedDeviceMode] = useState(
    deviceMode && deviceMode !== 'responsive' ? deviceMode : 'desktop-standard'
  );
  useEffect(() => {
    if (deviceMode && deviceMode !== 'responsive') {
      setLastFixedDeviceMode(deviceMode);
    }
  }, [deviceMode]);

  const handleResponsiveToggle = (enabled) => {
    if (typeof handleDeviceModeChange !== 'function') return;
    handleDeviceModeChange(enabled ? 'responsive' : (lastFixedDeviceMode || 'desktop-standard'));
  };

  /** Grouped options for the fixed device-size picker (responsive is the toggle above). */
  const devicePresetGroups = useMemo(() => {
    const dims = deviceDimensions || {};
    const entries = Object.entries(dims);
    const toOpts = (list) =>
      list.map(([key, d]) => ({
        value: key,
        label: d.name,
        description: `${d.width} × ${d.height}px`,
      }));
    const groups = [];
    const phones = entries.filter(([, d]) => d.category === 'phone');
    const tablets = entries.filter(([, d]) => d.category === 'tablet');
    const desktops = entries.filter(([, d]) => d.category === 'desktop');
    if (phones.length) {
      groups.push({ id: 'phone', label: 'Mobile', icon: Smartphone, options: toOpts(phones) });
    }
    if (tablets.length) {
      groups.push({ id: 'tablet', label: 'Tablets', icon: Tablet, options: toOpts(tablets) });
    }
    if (desktops.length) {
      groups.push({ id: 'desktop', label: 'Desktop', icon: Monitor, options: toOpts(desktops) });
    }
    return groups;
  }, [deviceDimensions]);

  // Get the currently selected question template object
  const getSelectedQuestionTemplateObj = () => {
    if (!selectedQuestionTemplate) return null;
    const all = [
      ...questionTemplates.system,
      ...questionTemplates.sponsor,
      ...questionTemplates.user,
      ...questionTemplates.assessment,
      ...questionTemplates.other.map(o => o.template)
    ];
    return all.find(t => String(t.id) === selectedQuestionTemplate) || null;
  };

  const selectedQTemplateObj = getSelectedQuestionTemplateObj();

  // Check if selected question template is deletable (user-created or assessment-level)
  const isSelectedQTemplateDeletable = selectedQTemplateObj && (
    questionTemplates.user.some(t => String(t.id) === selectedQuestionTemplate) ||
    questionTemplates.assessment.some(t => String(t.id) === selectedQuestionTemplate)
  );

  // Layout templates can target question elements OR instruction elements (FECOA-1335).
  // Other element types (button, divider) are not templatable.
  const isTemplatableElement = (el) => el?.type === 'question' || el?.type === 'instruction';

  // Count templatable elements compatible with the selected template across all screens
  // (excluding the current element). Compat-based — see questionTypeCompatible —
  // so a 'nominal' template counts both 'nominal' and 'multiple_choice' questions.
  const countSimilarElements = () => {
    if (!selectedQTemplateObj || !selectedElementData) return 0;
    const templateType = selectedQTemplateObj.question_type || getEffectiveQuestionType(selectedElementData);
    if (!templateType) return 0;
    let count = 0;
    safeScreens.forEach(screen => {
      screen.elements?.forEach(el => {
        if (!isTemplatableElement(el) || el.id === selectedElement) return;
        const elType = getEffectiveQuestionType(el);
        if (questionTypeCompatible(elType, templateType)) count++;
      });
    });
    return count;
  };

  const similarCount = selectedQTemplateObj ? countSimilarElements() : 0;

  // Apply selected template to ALL compatible question elements across all screens + set as default
  const applyTemplateToSimilarElements = async () => {
    if (!selectedQTemplateObj || !selectedElementData) return;
    const templateType = selectedQTemplateObj.question_type || getEffectiveQuestionType(selectedElementData);
    if (!templateType) return;
    const displayedType = displayType(getEffectiveQuestionType(selectedElementData));

    const config = { ...selectedQTemplateObj.layout_config };
    delete config.navigation;

    const canvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1280, height: 900 };
    // Collect the matching elements. An element id is stable; the screen_question_id
    // it carries is not, so the ids for the bulk-apply below are taken from the save
    // response instead (FECOA-2014).
    const matchedElements = [];
    const matchedElementsDebug = [];
    let lockedSkipCount = 0;
    let appliedCount = 0;
    safeScreens.forEach(screen => {
      screen.elements?.forEach(el => {
        if (!isTemplatableElement(el)) return;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return;
        if (el.isLocked) {
          lockedSkipCount += 1;
          return;
        }
        appliedCount += 1;
        matchedElements.push(el);
        matchedElementsDebug.push({
          id: el.id,
          elType,
          screen_question_id: el.screen_question_id,
          isUngrouped: !!el.isUngrouped,
          subElementCount: el.subElements?.length || 0,
        });
      });
    });
    console.log('[FECOA-1337] applyTemplateToSimilarElements', {
      templateType,
      templateId: selectedQTemplateObj.id,
      templateName: selectedQTemplateObj.name,
      configHasIsUngrouped: !!config.isUngrouped,
      configHasSubElementLayout: !!config.subElementLayout,
      matchedElements: matchedElementsDebug,
      lockedSkipCount,
    });

    // Client-side update for immediate UI feedback
    const updatedScreens = safeScreens.map(screen => ({
      ...screen,
      elements: screen.elements.map(el => {
        if (!isTemplatableElement(el) || el.isLocked) return el;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return el;
        return applyTemplateConfigToElement(el, config, canvasDims);
      })
    }));
    setScreens(updatedScreens);

    // Save before calling bulk-apply. save_screens writes SurveyScreen.layout_data,
    // the record the canvas and the participant renderer actually read (FECOA-1337),
    // and returns freshly minted screen_question_ids. It also deletes and recreates
    // every ScreenQuestion row, so an id cached on a canvas element can already point
    // at a deleted row (an undo, a second tab, or a racing auto-save is enough) and
    // the bulk-apply endpoint then 404s on every id it is given (FECOA-2014).
    let freshMapping = null;
    if (typeof handleSave === 'function') {
      const persisted = await handleSave(true, false, updatedScreens);
      if (persisted === false) {
        toast.error('Template applied on canvas but not saved \u2014 use Save to persist it.');
        return;
      }
      freshMapping = persisted?.element_screen_question_mapping || null;
    }

    // Backend bulk-apply annotates ScreenQuestion.position_data and writes the audit
    // record. It runs after the save so it can be addressed with the ids that save
    // just handed back.
    const screenQuestionIds = resolveScreenQuestionIds(matchedElements, freshMapping);
    let bulkApplyError = null;
    if (screenQuestionIds.length > 0) {
      try {
        const bulkResult = await api.post(
          `/api/assessment-builder/layout-templates/${selectedQTemplateObj.id}/bulk-apply/`,
          { screen_question_ids: screenQuestionIds }
        );
        console.log('[BULK_APPLY] Backend result:', bulkResult.data);
        const data = bulkResult.data || {};
        if (data.success === false) {
          bulkApplyError = (data.errors && data.errors[0]) || 'Backend bulk-apply failed';
        } else if (Array.isArray(data.errors) && data.errors.length > 0) {
          bulkApplyError = `${data.success_count} of ${screenQuestionIds.length}: ${data.errors[0]}`;
        } else if (typeof data.success_count === 'number' && data.success_count < screenQuestionIds.length) {
          bulkApplyError = `${data.success_count} of ${screenQuestionIds.length} questions annotated on the server`;
        }
      } catch (err) {
        console.warn('[BULK_APPLY] Backend bulk-apply failed (styles are already saved):', err);
        bulkApplyError = err?.response?.data?.errors?.[0] || err?.response?.data?.error || err?.message || 'Backend bulk-apply failed';
      }
    }

    if (bulkApplyError) {
      // The styles are already persisted by the save above. bulk-apply only
      // annotates ScreenQuestion.position_data, so a failure here is an annotation
      // gap rather than lost work, and must not claim nothing was saved (FECOA-2014).
      toast(`Template applied and saved. Server annotation incomplete: ${bulkApplyError}`, { icon: '\u26a0\ufe0f' });
    }

    // Also set as default for this question type
    try {
      await AssessmentBuilderAPI.setLayoutDefault({
        template_id: selectedQTemplateObj.id,
        assessment_id: assessmentId,
        question_type: templateType
      });
      if (onDefaultTemplateSet) {
        onDefaultTemplateSet(selectedQTemplateObj, templateType);
      }
      const skipSuffix = lockedSkipCount > 0 ? `, ${lockedSkipCount} skipped (locked)` : '';
      toast.success(`Applied to ${appliedCount} ${displayedType} elements${skipSuffix} and set as default`);
    } catch (err) {
      console.error('Failed to set default:', err);
      const skipSuffix = lockedSkipCount > 0 ? `, ${lockedSkipCount} skipped (locked)` : '';
      toast.success(`Applied to ${appliedCount} ${displayedType} elements${skipSuffix} (default not set)`);
    }
  };

  // Wrapper for TemplateManagerModal's "Use for Assessment" button.
  // Accepts a template object and applies it to all compatible question elements.
  const applyTemplateToAllOfType = async (template) => {
    const templateType = template.question_type || 'ordinal';
    const config = { ...template.layout_config };
    delete config.navigation;

    const canvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1280, height: 900 };
    const matchedElements = [];
    let lockedSkipCount = 0;
    let appliedCount = 0;
    safeScreens.forEach(screen => {
      screen.elements?.forEach(el => {
        if (!isTemplatableElement(el)) return;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return;
        if (el.isLocked) {
          lockedSkipCount += 1;
          return;
        }
        appliedCount += 1;
        matchedElements.push(el);
      });
    });

    const updatedScreens = safeScreens.map(screen => ({
      ...screen,
      elements: screen.elements.map(el => {
        if (!isTemplatableElement(el) || el.isLocked) return el;
        const elType = getEffectiveQuestionType(el);
        if (!questionTypeCompatible(elType, templateType)) return el;
        return applyTemplateConfigToElement(el, config, canvasDims);
      })
    }));
    setScreens(updatedScreens);

    // Save before calling bulk-apply. save_screens writes SurveyScreen.layout_data,
    // the record the canvas and the participant renderer actually read (FECOA-1337),
    // and returns freshly minted screen_question_ids. It also deletes and recreates
    // every ScreenQuestion row, so an id cached on a canvas element can already point
    // at a deleted row (an undo, a second tab, or a racing auto-save is enough) and
    // the bulk-apply endpoint then 404s on every id it is given (FECOA-2014).
    let freshMapping = null;
    if (typeof handleSave === 'function') {
      const persisted = await handleSave(true, false, updatedScreens);
      if (persisted === false) {
        toast.error('Template applied on canvas but not saved \u2014 use Save to persist it.');
        return;
      }
      freshMapping = persisted?.element_screen_question_mapping || null;
    }

    // Same ordering as applyTemplateToSimilarElements: persist first, then annotate
    // with the ids the save returned.
    const screenQuestionIds = resolveScreenQuestionIds(matchedElements, freshMapping);
    let bulkApplyError = null;
    if (screenQuestionIds.length > 0) {
      try {
        const bulkResult = await api.post(
          `/api/assessment-builder/layout-templates/${template.id}/bulk-apply/`,
          { screen_question_ids: screenQuestionIds }
        );
        console.log('[BULK_APPLY] Backend result:', bulkResult.data);
        const data = bulkResult.data || {};
        if (data.success === false) {
          bulkApplyError = (data.errors && data.errors[0]) || 'Backend bulk-apply failed';
        } else if (Array.isArray(data.errors) && data.errors.length > 0) {
          bulkApplyError = `${data.success_count} of ${screenQuestionIds.length}: ${data.errors[0]}`;
        } else if (typeof data.success_count === 'number' && data.success_count < screenQuestionIds.length) {
          bulkApplyError = `${data.success_count} of ${screenQuestionIds.length} questions annotated on the server`;
        }
      } catch (err) {
        console.warn('[BULK_APPLY] Backend bulk-apply failed (styles are already saved):', err);
        bulkApplyError = err?.response?.data?.errors?.[0] || err?.response?.data?.error || err?.message || 'Backend bulk-apply failed';
      }
    }

    if (bulkApplyError) {
      // The styles are already persisted by the save above. bulk-apply only
      // annotates ScreenQuestion.position_data, so a failure here is an annotation
      // gap rather than lost work, and must not claim nothing was saved (FECOA-2014).
      toast(`Template applied and saved. Server annotation incomplete: ${bulkApplyError}`, { icon: '\u26a0\ufe0f' });
    }

    await AssessmentBuilderAPI.setLayoutDefault({
      template_id: template.id,
      assessment_id: assessmentId,
      question_type: templateType
    });
    if (onDefaultTemplateSet) {
      onDefaultTemplateSet(template, templateType);
    }
    const skipSuffix = lockedSkipCount > 0 ? `, ${lockedSkipCount} skipped (locked)` : '';
    toast.success(`Applied to ${appliedCount} ${displayType(templateType)} elements${skipSuffix}`);
  };

  // Delete a question layout template
  const handleDeleteQuestionTemplate = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'question') return;
    try {
      await api.delete(`/api/assessment-builder/layout-templates/${deleteConfirm.id}/`);
      toast.success('Template deleted');
      setDeleteConfirm(null);
      setDeleteTypedName('');
      setSelectedQuestionTemplate('');
      // Re-fetch templates
      const questionType = getEffectiveQuestionType(selectedElementData);
      const response = await api.get('/api/assessment-builder/layout-templates/available/', {
        params: { question_type: questionType, assessment_id: assessmentId }
      });
      setQuestionTemplates({
        system: response.data.system_templates || [],
        sponsor: response.data.sponsor_templates || [],
        user: response.data.user_templates || [],
        assessment: response.data.assessment_templates || [],
        other: response.data.assessment_templates_from_other_assessments || []
      });
      // The Manage Assessment Templates modal reads the mount-time preload cache,
      // not this panel's list — invalidate it or the deleted template lingers
      // there until a full page reload (FECOA-1844).
      if (refreshTemplatesCache) refreshTemplatesCache();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Delete a button layout template
  const handleDeleteButtonTemplateConfirmed = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'button') return;
    try {
      await handleDeleteButtonTemplate(deleteConfirm.id);
      setDeleteConfirm(null);
      setDeleteTypedName('');
    } catch (error) {
      console.error('Failed to delete button template:', error);
    }
  };

  // Check if selected button template is deletable (user or assessment level)
  const selectedBtnTemplateObj = templates.find(t => String(t.id) === String(selectedButtonTemplate));
  const isSelectedBtnTemplateDeletable = selectedBtnTemplateObj &&
    (selectedBtnTemplateObj.level === 'user' || selectedBtnTemplateObj.level === 'assessment');

  // Fetch layout templates when a templatable element (question or instruction)
  // is selected. Gating on type === 'question' left the dropdown empty for
  // instruction elements until an unrelated save repopulated it (FECOA-1335).
  useEffect(() => {
    const fetchQuestionTemplates = async () => {
      if (!isTemplatableElement(selectedElementData)) return;
      const questionType = getEffectiveQuestionType(selectedElementData);
      setLoadingTemplates(true);
      try {
        const response = await api.get('/api/assessment-builder/layout-templates/available/', {
          params: {
            question_type: questionType,
            assessment_id: assessmentId
          }
        });
        setQuestionTemplates({
          system: response.data.system_templates || [],
          sponsor: response.data.sponsor_templates || [],
          user: response.data.user_templates || [],
          assessment: response.data.assessment_templates || [],
          other: response.data.assessment_templates_from_other_assessments || []
        });
      } catch (error) {
        console.error('Failed to fetch question templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchQuestionTemplates();
  }, [selectedElementData?.id, selectedElementData?.data?.type, selectedElementData?.responseConfig?.layout, assessmentId]);
  
  // Apply a question layout template
  const applyQuestionTemplate = (template) => {
    if (!template?.layout_config || !selectedElement) return;

    const { navigation: _nav, ...config } = template.layout_config;
    const canvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1280, height: 900 };

    const updatedScreens = safeScreens.map((screen, idx) => {
      if (idx !== currentScreenIndex) return screen;
      return {
        ...screen,
        elements: screen.elements.map(el =>
          el.id === selectedElement ? applyTemplateConfigToElement(el, config, canvasDims) : el
        )
      };
    });
    setScreens(updatedScreens);
  };

  // Scale layouts render from responseConfig (min/max/anchors), not from a responseOptions array —
  // allow template apply/save for them even when responseOptions is empty.
  const CONFIG_DRIVEN_SCALE_LAYOUTS = ['nrs', 'vas', 'thermometer', 'vrs-unipolar', 'vrs-bipolar', 'segmented', 'stepped-slider', 'emoji'];
  // Text-input question types have no responseOptions but still have a layout (position, size, textarea config) worth templating.
  const TEXT_INPUT_QUESTION_TYPES = ['text', 'free_text'];
  // Body maps render a fixed region set instead of responseOptions, but their layout (size,
  // question styling, where the Selected Areas panel sits) is still worth templating (FECOA-2014).
  const FIXED_REGION_QUESTION_TYPES = ['body_map'];
  const resolvedElementType = getElementQuestionType(selectedElementData);
  const hasTemplatableResponseUI =
    // Instructions are placed on the canvas as type='instruction' (not 'question'), but they
    // still have layout (position, font, color, padding) worth templating (FECOA-1335).
    selectedElementData?.type === 'instruction' ||
    (selectedElementData?.type === 'question' && (
      selectedElementData.responseOptions?.length > 0 ||
      (selectedElementData.isUngrouped && selectedElementData.subElements?.length > 0) ||
      CONFIG_DRIVEN_SCALE_LAYOUTS.includes(selectedElementData.responseConfig?.layout) ||
      TEXT_INPUT_QUESTION_TYPES.includes(resolvedElementType) ||
      FIXED_REGION_QUESTION_TYPES.includes(resolvedElementType)
    ));

  // UNIFIED LAYOUT: always show screen-level sections, conditionally show element section below
  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">

      {/* Canvas size — responsive toggle (takes over) + fixed device presets */}
      <div className="card flex-shrink-0 p-4">
        {/* Responsive toggle — always available; overrides the device size below */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">
              <MonitorSmartphone className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={2} />
              Responsive canvas
            </div>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400 pl-6">
              Fills the full width of any screen. Overrides the device size.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isResponsiveCanvas}
            aria-label="Responsive canvas"
            onClick={() => handleResponsiveToggle(!isResponsiveCanvas)}
            disabled={typeof handleDeviceModeChange !== 'function'}
            className={`relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-gray-800 ${
              isResponsiveCanvas ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-white/15'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-150 dark:bg-white ${
                isResponsiveCanvas ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Fixed device-size picker — hidden while the responsive canvas is on */}
        {isResponsiveCanvas ? (
          <p className="rounded-lg border border-gray-200/70 bg-gray-50 text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300 mt-3 p-3 text-[12.5px] leading-relaxed">
            The canvas adapts to each device's width. Switch this off to design for a fixed device size.
          </p>
        ) : (
          <div className="mt-3">
            {devicePresetGroups.length > 0 && typeof handleDeviceModeChange === 'function' ? (
              <GroupedListboxSelect
                label="Canvas size"
                hint="Preview dimensions for the builder canvas, grids, and layout templates."
                value={deviceMode}
                onChange={handleDeviceModeChange}
                groups={devicePresetGroups}
                placeholder="Choose a device preset…"
              />
            ) : (
              <p className="text-[12.5px] text-gray-500 dark:text-gray-400">No device presets are available for this study.</p>
            )}
          </div>
        )}
      </div>

      {/* ── Form Formatting Section ── always visible ── (FECOA-732) */}
      {typeof onGlobalFormattingChange === 'function' && (
        <FormFormattingSection
          globalFormatting={globalFormatting}
          onGlobalFormattingChange={onGlobalFormattingChange}
        />
      )}

      {/* ── Assessment Templates Section ── always visible ── */}
      <div className="card flex-shrink-0 p-4">
        <div className="mb-3 flex items-start gap-2">
          <Settings className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          <div className="min-w-0">
            <h3 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Assessment Templates</h3>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">Question layout templates for this assessment</p>
          </div>
        </div>
        <button
          onClick={() => setShowTemplateManager(true)}
          className="btn btn-secondary btn-sm h-auto min-h-8 w-full whitespace-normal py-1.5 leading-snug"
        >
          <Settings className="h-3.5 w-3.5 flex-shrink-0" />
          Manage Assessment Templates
        </button>
      </div>

      {/* ── Screen Button Layout Section ── visible when a button element is selected ── */}
      {selectedElementData?.type === 'button' && <div className="card flex-shrink-0 p-4">
        <div className="mb-4 flex items-start gap-2">
          <Layout className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          <div className="min-w-0">
            <h3 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Screen Button Layout</h3>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">Apply consistent navigation to all screens</p>
          </div>
        </div>
        <div className="space-y-4">
          {/* Load Template Dropdown */}
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <Copy className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <div className="min-w-0">
                <label className="block text-[13px] font-medium text-gray-900 dark:text-gray-100">Apply a Button Template</label>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">Copy a saved button layout onto this screen</p>
              </div>
            </div>
            <div className="relative">
              <select
                value={selectedButtonTemplate}
                onChange={handleLoadButtonTemplate}
                className="input h-9 appearance-none pr-9"
              >
                <option value="">Choose a button template...</option>
                {templates.filter(t => t.level === 'user').length > 0 && (
                  <optgroup label="Your Personal Templates">
                    {templates.filter(t => t.level === 'user').map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
                {templates.filter(t => t.level === 'assessment').length > 0 && (
                  <optgroup label="This Assessment">
                    {templates.filter(t => t.level === 'assessment').map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
                {templates.filter(t => t.level === 'sponsor').length > 0 && (
                  <optgroup label="Sponsor Templates">
                    {templates.filter(t => t.level === 'sponsor').map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
                {templates.filter(t => t.level === 'system').length > 0 && (
                  <optgroup label="System Templates">
                    {templates.filter(t => t.level === 'system').map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
            {selectedButtonTemplate && (
              <div className="space-y-2">
                <button
                  onClick={handleApplyTemplateToAllScreens}
                  className="btn btn-primary btn-sm h-auto min-h-8 w-full whitespace-normal py-1.5 leading-snug"
                >
                  Apply to All {safeScreens.length} Screens
                </button>
                {isSelectedBtnTemplateDeletable && (
                  <button
                    onClick={() => {
                      setDeleteConfirm({ id: selectedBtnTemplateObj.id, name: selectedBtnTemplateObj.name, type: 'button' });
                      setDeleteTypedName('');
                    }}
                    className="btn btn-danger-soft btn-sm w-full"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete This Template
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Save Current Layout as Template */}
          <div className="space-y-3 border-t border-gray-100 pt-4 dark:border-white/[0.06]">
            <div className="flex items-start gap-2">
              <Save className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <div className="min-w-0">
                <label className="block text-[13px] font-medium text-gray-900 dark:text-gray-100">Save Button Layout</label>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">Capture current button positions as a reusable template</p>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder='Template name (e.g., "Standard Navigation")'
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                className="input h-9 min-w-0 flex-1"
              />
              <button
                onClick={handleSaveButtonTemplate}
                disabled={!newTemplateName.trim()}
                className="btn btn-secondary btn-sm h-9 flex-shrink-0"
              >
                Save
              </button>
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Save to:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTemplateSaveLevel('user')}
                  className={`rounded-xl border px-3 py-2.5 text-left text-[13px] font-medium text-gray-900 transition-[border-color,box-shadow] duration-150 dark:text-gray-100 ${
                    templateSaveLevel === 'user'
                      ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                      : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
                  }`}
                >
                  My Library
                  <span className="mt-0.5 block text-[11.5px] font-normal leading-snug text-gray-500 dark:text-gray-400">Reuse across all assessments</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateSaveLevel('assessment')}
                  className={`rounded-xl border px-3 py-2.5 text-left text-[13px] font-medium text-gray-900 transition-[border-color,box-shadow] duration-150 dark:text-gray-100 ${
                    templateSaveLevel === 'assessment'
                      ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                      : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
                  }`}
                >
                  This Assessment
                  <span className="mt-0.5 block text-[11.5px] font-normal leading-snug text-gray-500 dark:text-gray-400">Private to this assessment</span>
                </button>
              </div>
            </div>
            <ToggleRow
              title="Set as default button layout for new screens in this assessment"
              checked={setTemplateAsDefault}
              onChange={(e) => setSetTemplateAsDefault(e.target.checked)}
            />
          </div>

          {/* Help Text */}
          <div className="rounded-lg border border-gray-200/70 bg-gray-50 text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300 p-3.5 text-[12.5px] leading-relaxed">
            <strong className="font-semibold text-gray-800 dark:text-gray-100">Button layouts are separate from Assessment Templates.</strong> Position your Back/Next buttons, save as a <em>button template</em>, then apply it across all screens. Save to <strong className="font-semibold text-gray-800 dark:text-gray-100">My Library</strong> to reuse the same layout in future assessments.
          </div>
        </div>
      </div>}

      {/* ── Element Layout Controls ── only when the selected element has layout content ── */}
      {hasTemplatableResponseUI && (
        <div className="card flex-shrink-0 p-4">
          <div className="mb-4 flex items-start gap-2">
            <Move className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <div className="min-w-0">
              <h3 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Element Layout</h3>
              <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">
                {selectedElementData.type === 'question' ? 'Templates & alignment' : 'Layout templates'}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            {/* Question Layout Template (for questions only) */}
            {hasTemplatableResponseUI && (
              <div>
                <div className="mb-3 flex items-start gap-2">
                  <Layers className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <div className="min-w-0">
                    <h4 className="text-[13px] font-medium text-gray-900 dark:text-gray-100">Apply Layout Template</h4>
                    <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">Apply a saved layout to this and similar questions</p>
                  </div>
                </div>
                {loadingTemplates ? (
                  <div className="flex items-center justify-center gap-2 py-3 text-gray-500 dark:text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                    <span className="text-xs">Loading templates...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <select
                        value={selectedQuestionTemplate}
                        onChange={(e) => {
                          const templateId = e.target.value;
                          setSelectedQuestionTemplate(templateId);
                          if (templateId) {
                            const allTemplates = [
                              ...questionTemplates.system,
                              ...questionTemplates.sponsor,
                              ...questionTemplates.user,
                              ...questionTemplates.assessment,
                              ...questionTemplates.other.map(o => o.template)
                            ];
                            const template = allTemplates.find(t => String(t.id) === templateId);
                            if (template) applyQuestionTemplate(template);
                          }
                        }}
                        className="input h-9 appearance-none pr-9"
                      >
                        {(() => {
                          const filterByDevice = (templates) => templates.filter(t =>
                            matchesTemplateDevice(t, currentDeviceCategory)
                          );
                          const filteredSystem = filterByDevice(questionTemplates.system);
                          const filteredSponsor = filterByDevice(questionTemplates.sponsor);
                          const filteredUser = filterByDevice(questionTemplates.user);
                          const filteredAssessment = filterByDevice(questionTemplates.assessment);
                          const filteredOther = questionTemplates.other.filter(o =>
                            matchesTemplateDevice(o.template, currentDeviceCategory)
                          );

                          // Disambiguate duplicate names in user templates
                          const getUniqueTemplateName = (template, index, templates) => {
                            const duplicates = templates.filter(t => t.name === template.name);
                            if (duplicates.length <= 1) return template.name;
                          
                            // If there are duplicates, add a suffix
                            const duplicateIndex = duplicates.findIndex(t => t.id === template.id);
                            if (duplicateIndex === 0) {
                              // First duplicate: add date if available
                              if (template.created_at) {
                                const date = new Date(template.created_at);
                                const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                return `${template.name} (${dateStr})`;
                              }
                            }
                            // For other duplicates or no date: add numeric suffix
                            return `${template.name} (${duplicateIndex + 1})`;
                          };
                          return (
                            <>
                              <option value="">Select a template...</option>
                              {filteredUser.length > 0 && (
                                <optgroup label="Your Templates">
                                  {filteredUser.map((t, index) => (
                                    <option key={t.id} value={t.id}>{getUniqueTemplateName(t, index, filteredUser)}</option>
                                  ))}
                                </optgroup>
                              )}
                              {filteredAssessment.length > 0 && (
                                <optgroup label="This Assessment">
                                  {filteredAssessment.map((t, index) => (
                                    <option key={t.id} value={t.id}>{getUniqueTemplateName(t, index, filteredAssessment)}</option>
                                  ))}
                                </optgroup>
                              )}
                              {filteredSystem.length > 0 && (
                                <optgroup label="System Templates">
                                  {filteredSystem.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                  ))}
                                </optgroup>
                              )}
                              {filteredSponsor.length > 0 && (
                                <optgroup label="Sponsor Templates">
                                  {filteredSponsor.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                  ))}
                                </optgroup>
                              )}
                              {filteredOther.length > 0 && (
                                <optgroup label="From Other Assessments">
                                  {filteredOther.map(o => (
                                    <option key={o.template.id} value={o.template.id}>
                                      {o.assessment_name}: {o.template.name}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                            </>
                          );
                        })()}
                      </select>
                      <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                    {selectedQuestionTemplate && selectedQTemplateObj && (
                      <div className="space-y-2">
                        {similarCount > 0 && (
                          <button
                            onClick={applyTemplateToSimilarElements}
                            className="btn btn-primary btn-sm h-auto min-h-8 w-full whitespace-normal py-1.5 leading-snug"
                          >
                            Apply to All {similarCount + 1} {displayType(selectedElementData.data?.type) || 'Similar'} Elements
                          </button>
                        )}
                        {similarCount === 0 && (
                          <button
                            onClick={applyTemplateToSimilarElements}
                            className="btn btn-primary btn-sm h-auto min-h-8 w-full whitespace-normal py-1.5 leading-snug"
                          >
                            Set as Default for {displayType(selectedElementData.data?.type) || 'This Type'}
                          </button>
                        )}
                        <p className="text-center text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                          {similarCount > 0
                            ? `Applies to this element + ${similarCount} other ${displayType(selectedElementData.data?.type)} question${similarCount > 1 ? 's' : ''} and sets as default`
                            : 'New questions of this type will use this template automatically'}
                        </p>
                        {isSelectedQTemplateDeletable && (
                          <button
                            onClick={() => {
                              setDeleteConfirm({ id: selectedQTemplateObj.id, name: selectedQTemplateObj.name, type: 'question' });
                              setDeleteTypedName('');
                            }}
                            className="btn btn-danger-soft btn-sm w-full"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete This Template
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Save as Question Layout Template (for questions only) */}
            {hasTemplatableResponseUI && (
              <SaveQuestionTemplateSection
                selectedElementData={selectedElementData}
                assessmentId={assessmentId}
                deviceMode={deviceMode}
                onTemplateSaved={async () => {
                  // A newly saved template must also reach the mount-time preload
                  // cache backing the Manage Assessment Templates modal; without
                  // this the first saved template never shows there until a page
                  // reload (FECOA-1844).
                  if (refreshTemplatesCache) refreshTemplatesCache();
                  const questionType = getEffectiveQuestionType(selectedElementData);
                  setLoadingTemplates(true);
                  try {
                    const response = await api.get('/api/assessment-builder/layout-templates/available/', {
                      params: { question_type: questionType, assessment_id: assessmentId }
                    });
                    setQuestionTemplates({
                      system: response.data.system_templates || [],
                      sponsor: response.data.sponsor_templates || [],
                      user: response.data.user_templates || [],
                      assessment: response.data.assessment_templates || [],
                      other: response.data.assessment_templates_from_other_assessments || []
                    });
                  } catch (error) {
                    console.error('Failed to refresh templates:', error);
                  } finally {
                    setLoadingTemplates(false);
                  }
                }}
              />
            )}

          </div>
        </div>
      )}

      <TemplateManagerModal
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
        assessmentId={assessmentId}
        preloadedTemplates={allTemplatesCache}
        appliedDefaultTemplateIds={appliedDefaultTemplateIds}
        selectedElementData={selectedElementData}
        applyQuestionTemplate={applyQuestionTemplate}
        applyTemplateToAllOfType={applyTemplateToAllOfType}
        screens={screens}
        currentScreenIndex={currentScreenIndex}
        deviceMode={deviceMode}
        onTemplateUpdated={() => {
          // Refresh the parent cache so the modal picks up applied defaults
          if (refreshTemplatesCache) refreshTemplatesCache();
          if (isTemplatableElement(selectedElementData)) {
            const fetchQuestionTemplates = async () => {
              const questionType = getEffectiveQuestionType(selectedElementData);
              setLoadingTemplates(true);
              try {
                const response = await api.get('/api/assessment-builder/layout-templates/available/', {
                  params: {
                    question_type: questionType,
                    assessment_id: assessmentId
                  }
                });
                setQuestionTemplates({
                  system: response.data.system_templates || [],
                  sponsor: response.data.sponsor_templates || [],
                  user: response.data.user_templates || [],
                  assessment: response.data.assessment_templates || [],
                  other: response.data.assessment_templates_from_other_assessments || []
                });
              } catch (error) {
                console.error('Failed to fetch question templates:', error);
              } finally {
                setLoadingTemplates(false);
              }
            };
            fetchQuestionTemplates();
          }
        }}
      />

      {/* Delete Confirmation Modal (shared for button + question templates) */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-950/40 dark:bg-black/70 flex items-center justify-center p-4 z-[10000]">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
            <div className="flex items-center gap-2.5 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-danger-600 dark:text-danger-400" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Delete Template</h3>
            </div>
            <div className="px-6 py-5">
              <p className="mb-3 text-[13.5px] leading-relaxed text-gray-600 dark:text-gray-300">
                This action cannot be undone. To confirm, type the template name below:
              </p>
              <div className="mb-3 rounded-lg border border-gray-200/70 bg-gray-50 px-3 py-2 dark:border-white/[0.07] dark:bg-white/[0.03]">
                <span className="break-all font-mono text-[13px] font-medium text-gray-800 dark:text-gray-100">{deleteConfirm.name}</span>
              </div>
              <input
                type="text"
                placeholder="Type template name to confirm..."
                value={deleteTypedName}
                onChange={(e) => setDeleteTypedName(e.target.value)}
                className="input"
                autoFocus
              />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteTypedName(''); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm.type === 'question') handleDeleteQuestionTemplate();
                  else if (deleteConfirm.type === 'button') handleDeleteButtonTemplateConfirmed();
                }}
                disabled={deleteTypedName !== deleteConfirm.name}
                className="btn btn-danger"
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LayoutPanel;